import React, { useEffect, useMemo, useState } from "react";
import { Ticket, Loader2, Search, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card";
import { Picker } from "../../components/ui/Picker";
import { Input } from "../../components/ui/Input";
import { ToggleSwitch } from "../../components/ui/ToggleSwitch";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { SettingsBanner } from "../../components/ui/SettingsBanner";
import { ticketProvidersApi } from "../../utils/api";
import { apiErrorMessage } from "../../utils/helpers";

const PAGE_SIZE = 10;

// Human judgement only — nothing infers what kind of site a provider is, so every row starts
// unclassified and stays there until someone looks.
const CATEGORIES = [
  { value: "unclassified", label: "Unclassified" },
  { value: "ticketing", label: "Ticketing" },
  { value: "event_listing", label: "Event listing" },
  { value: "venue", label: "Venue" },
  { value: "resale", label: "Resale" },
];

// "12 events - Madrid, Paris", or "No active events" for a provider whose events have all expired
// (its row stays, so its block and category survive a quiet season).
const describeReach = ({ event_count: count, cities = [] }) => {
  if (!count) return "No active events";
  const events = `${count} ${count === 1 ? "event" : "events"}`;
  return cities.length > 0 ? `${events} - ${cities.join(", ")}` : events;
};

// Reach is as of the last registry refresh, not as of now — say which, rather than implying live.
const describeFreshness = (providers) => {
  const stamps = providers.map((p) => p.refreshed_at).filter(Boolean).sort();
  if (stamps.length === 0) return "Never refreshed - run the registry refresh to fill this list.";
  return `Reach as of ${new Date(stamps[stamps.length - 1]).toLocaleString()}.`;
};

// The list is one Postgres read. `refresh_ticket_providers` (a pipeline step, or the button
// here) is what rebuilds each provider's site, event count and cities from the event index;
// the block and the category belong to whoever set them and no refresh overwrites them.
export default function TicketProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [blockedFilter, setBlockedFilter] = useState("");
  const [ruleFilter, setRuleFilter] = useState("");
  const [page, setPage] = useState(1);

  // Every other settings card clears its banner after 4s (useToggleableSetting); this one owns
  // its state because it is a list with per-row PATCHes, so it has to do the same by hand.
  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  // Rejects on failure rather than swallowing it: `SettingsBanner` renders `message` and `error`
  // as two separate divs, so a caller that reported success over a failed read-back would put
  // both claims on screen at once.
  const load = () => ticketProvidersApi.getAll().then(setProviders);

  // One action at a time: every control on the card is disabled while a save (with its re-read
  // after a 409) or a Refresh is in flight, so no two overlap and one's list or banner can never
  // undo another's.
  const busy = saving || refreshing;

  useEffect(() => {
    load()
      .catch((err) => {
        console.error("Failed to load ticket providers", err);
        setError("Failed to load ticket providers");
      })
      .finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    setMessage(null);
    try {
      const { providers: found } = await ticketProvidersApi.refresh();
      // The rebuild landed; only the re-read can still fail, and saying so beats a success
      // banner sitting beside a stale list. Nested so one `finally` owns the spinner.
      try {
        await load();
        setMessage(`Registry refreshed - ${found} ${found === 1 ? "provider" : "providers"} found.`);
      } catch {
        setError("Registry refreshed, but the list could not be reloaded.");
      }
    } catch {
      setError("Failed to refresh the provider registry");
    } finally {
      setRefreshing(false);
    }
  };

  // Category is its own control, so it is deliberately not part of the free-text match: typing
  // "venue" should find a provider called that, not every venue-classified row.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return providers.filter((p) => {
      if (categoryFilter && (p.category || "unclassified") !== categoryFilter) return false;
      if (blockedFilter && String(!!p.blocked) !== blockedFilter) return false;
      if (ruleFilter === "rule" && !p.image_rule) return false;
      // A disabled rule applies nothing, whatever its switch says.
      if (ruleFilter === "auto" && (!p.image_rule?.auto_apply || p.image_rule?.disabled)) return false;
      if (!term) return true;
      return (
        p.provider.toLowerCase().includes(term) ||
        (p.cities || []).some((city) => city.toLowerCase().includes(term))
      );
    });
  }, [providers, search, categoryFilter, blockedFilter, ruleFilter]);

  // Paging is client-side: the whole registry is already in hand (one table read), so slicing it
  // costs nothing and a page change needs no request.
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Narrowing the filters can leave `page` past the end; clamping on render alone would show the
  // right rows while the control still highlighted a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, blockedFilter, ruleFilter]);

  const blockedCount = providers.filter((p) => p.blocked).length;

  // Every row action is the same shape: mark the card busy, PATCH, merge the returned row over
  // the listed one, clear up. Only the call and the wording differ.
  const save = async (provider, request, { onDone, failure }) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await request();
      setProviders((prev) => prev.map((p) => (p.provider === provider ? { ...p, ...updated } : p)));
      if (onDone) setMessage(onDone);
    } catch (err) {
      const status = err?.response?.status;
      // A refusal the admin can act on (4xx) says why in the API's words; a server error keeps the
      // action's own message, never the exception text the route puts in `detail`.
      const reason = status >= 400 && status < 500 ? apiErrorMessage(err, failure) : failure;
      setError(reason);
      // A 409 means the row changed under this page (a rule disabled since it loaded): re-read the
      // list so the row shows that, rather than offering the same refused switch again. Awaited, so
      // the card stays busy until it lands.
      if (status === 409) {
        try {
          await load();
        } catch {
          const sentence = /[.!?]$/.test(reason) ? reason : `${reason}.`;
          setError(`${sentence} The list could not be reloaded: reload the page to see its current state.`);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const toggle = (provider, next) =>
    save(provider, () => ticketProvidersApi.setBlocked(provider, next), {
      onDone: `${provider} ${next ? "blocked" : "unblocked"}. Takes up to a minute to reach the web app.`,
      failure: `Failed to ${next ? "block" : "unblock"} ${provider}`,
    });

  // A provider image rule (only on rows the API marks with `image_rule`): on, accepting that
  // provider's link in event research adds the event's image, and the rule-image sweep can fill
  // open alerts; off, the rule only leads the image suggestions.
  const switchImageRule = (provider, next) =>
    save(provider, () => ticketProvidersApi.setImageRule(provider, next), {
      onDone: next
        ? `${provider} event images now apply automatically. Takes up to a minute to reach every API worker.`
        : `${provider} event images are back to suggestions only.`,
      failure: `Failed to switch the ${provider} image rule`,
    });

  const classify = (provider, category) =>
    save(provider, () => ticketProvidersApi.setCategory(provider, category), {
      // Confirms like every other write here: the select showing the new value proves nothing
      // on its own, since the row is merged locally either way.
      onDone: `${provider} classified as ${CATEGORIES.find((c) => c.value === category)?.label || category}.`,
      failure: `Failed to classify ${provider}`,
    });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Ticket Providers</CardTitle>
        </div>
        <CardDescription>
          Every provider found on stored event ticket links, with the cities it had active events in.
          Blocking one hides its links everywhere on the consumer app and stops it being accepted as a
          ticket candidate. Nothing is deleted — unblocking brings the links straight back. The category
          is yours to set; nothing infers it. A provider with an image rule (its event pages state the
          event's own image) also gets an <strong>Auto image</strong> switch: on, that image is added to
          events that have none or an open image alert; off, it is only offered first among the
          suggestions. Before switching one on, check a few of its suggestions in Image review: they
          are the ones badged "&lt;provider&gt; event image".
          {providers.length > 0 ? ` ${blockedCount} of ${providers.length} blocked. ` : " "}
          {describeFreshness(providers)}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SettingsBanner message={message} error={error} />
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : providers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ticket providers found in the event index yet.</p>
        ) : (
          <div className="space-y-4">
            {/* Wraps rather than squeezing: search + two filters + Refresh do not fit one row
                on a narrow window, and the search box is the one that would collapse. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[12rem] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Filter by provider or city..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Picker
                label="Filter by block status"
                value={blockedFilter}
                onChange={(e) => setBlockedFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Blocked</option>
                <option value="false">Not blocked</option>
              </Picker>
              <Picker
                label="Filter by image rule"
                value={ruleFilter}
                onChange={(e) => setRuleFilter(e.target.value)}
              >
                <option value="">Any image rule</option>
                <option value="rule">Has an image rule</option>
                <option value="auto">Auto image on</option>
              </Picker>
              <Picker
                label="Filter by type"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All types</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Picker>
              <Button variant="outline" onClick={refresh} disabled={busy}>
                <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>

            {visible.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No provider matches {search ? `"${search}"` : "this filter"}
                {categoryFilter && search ? " in this type" : ""}.
              </p>
            ) : (
              <div className="space-y-2">
                {pageRows.map((provider) => (
                  <ToggleSwitch
                    key={provider.provider}
                    label={provider.provider}
                    labelHref={provider.url}
                    description={describeReach(provider)}
                    checked={provider.blocked}
                    danger
                    checkedLabel="Blocked"
                    disabled={busy}
                    onToggle={() => toggle(provider.provider, !provider.blocked)}
                    trailing={
                      <>
                        {provider.image_rule ? (
                          <label
                            className={`flex items-center gap-1.5 whitespace-nowrap text-xs${
                              provider.image_rule.disabled ? " text-destructive" : ""
                            }`}
                            title={
                              provider.image_rule.disabled
                                ? `Rule disabled, it applies and suggests nothing: ${provider.image_rule.disabled}. It can be switched off, not on.`
                                : "Add the image this provider's event page states to events with no image or an open image alert"
                            }
                          >
                            <input
                              type="checkbox"
                              aria-label={`Auto image for ${provider.provider}`}
                              checked={provider.image_rule.auto_apply}
                              // A disabled rule's switch can only be turned off (the API refuses on).
                              disabled={
                                busy ||
                                (!!provider.image_rule.disabled && !provider.image_rule.auto_apply)
                              }
                              onChange={(e) => switchImageRule(provider.provider, e.target.checked)}
                            />
                            {provider.image_rule.disabled ? "Auto image (rule disabled)" : "Auto image"}
                          </label>
                        ) : null}
                        <Picker
                          compact
                          label={`Category for ${provider.provider}`}
                          value={provider.category || "unclassified"}
                          disabled={busy}
                          onChange={(e) => classify(provider.provider, e.target.value)}
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Picker>
                      </>
                    }
                  />
                ))}
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
